import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HomeDesigntemplateService, Accessory } from './home-designtemplate.service';
import { SnackbarComponent } from 'src/app/shared/components/snackbar/snackbar.component';

export type TemplateTab = 'all' | 'free' | 'paid';

@Component({
  selector: 'app-home-designtemplate',
  templateUrl: './home-designtemplate.component.html',
  styleUrls: ['./home-designtemplate.component.scss'],
  standalone: false,
})
export class HomeDesigntemplateComponent implements OnDestroy {
  freeTemplates: Accessory[] = [];
  paidTemplates: Accessory[] = [];
  allTemplates: Accessory[] = [];
  activeTab: TemplateTab = 'all';

  isLoading = true;
  hasError = false;
  host_url: string = environment.imageURL;
  brokenImageIds = new Set<string>();
  private sub = new Subscription();

  constructor(
    private router: Router,
    private templateService: HomeDesigntemplateService,
    private __snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  setTab(tab: TemplateTab): void {
    this.activeTab = tab;
  }

  get currentTemplates(): Accessory[] {
    if (this.activeTab === 'free') {
      return this.freeTemplates;
    }
    if (this.activeTab === 'paid') {
      return this.paidTemplates;
    }
    return this.allTemplates;
  }

  isFree(template: Accessory): boolean {
    return !template.price || template.price === 0;
  }

  loadTemplates(): void {
    this.isLoading = true;
    this.hasError = false;

    // Fetch up to 20 each to ensure we have sufficient items after filtering out broken/empty images.
    this.sub.add(
      forkJoin({
        free: this.templateService.getFreeAccessories(20),
        paid: this.templateService.getPaidAccessories(20),
      }).subscribe({
        next: (res) => {
          const filterValid = (items: Accessory[]) =>
            (items || []).filter((t) => {
              const img = t.thumbnailUrl || t.imageUrl;
              return Boolean(
                img &&
                  img.trim() !== '' &&
                  img !== 'null' &&
                  img !== 'undefined'
              );
            });

          this.freeTemplates = filterValid(res.free?.data || []).slice(0, 12);
          this.paidTemplates = filterValid(res.paid?.data || []).slice(0, 12);

          // Cap the combined tab at two rows of six items on desktop.
          const merged: Accessory[] = [];
          const maxLen = Math.max(
            this.freeTemplates.length,
            this.paidTemplates.length
          );
          for (let i = 0; i < maxLen; i++) {
            if (i < this.freeTemplates.length) merged.push(this.freeTemplates[i]);
            if (i < this.paidTemplates.length) merged.push(this.paidTemplates[i]);
          }
          this.allTemplates = merged.slice(0, 12);

          this.isLoading = false;
        },
        error: (err) => {
          console.error('Lỗi khi tải dữ liệu thiết kế:', err);
          this.isLoading = false;
          this.hasError = true;
          this.showSnackbar(
            this.translate.instant('common.ERROR_CONNECTION'),
            'error'
          );
        },
      })
    );
  }

  getTemplateImageUrl(template: Accessory): string {
    const imgPath = template.thumbnailUrl || template.imageUrl;
    if (!imgPath) return '';
    return imgPath.startsWith('http') ? imgPath : this.host_url + imgPath;
  }

  onImageError(id: string): void {
    if (id) {
      this.brokenImageIds.add(id);
    }
  }

  onSelectTemplate(template: Accessory): void {
    this.router.navigate(['/product'], {
      queryParams: {
        personalize: true,
        templateId: template.id,
      },
      state: { fromUrl: this.router.url },
    });
  }

  private showSnackbar(message: string, type: 'success' | 'error') {
    this.__snackBar.openFromComponent(SnackbarComponent, {
      data: { message, type },
      duration: 6000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['custom-snackbar'],
    });
  }
}
